'use client';

import { type FC, useState } from 'react';

import Image from 'molecules/Image';
import styles from 'molecules/LeaderShipCard/leadershipcard.module.scss';
import Link from 'molecules/Link';

import type { LeaderShipCardProps } from 'molecules/LeaderShipCard/config';

interface LeaderShipCardPropsWithRatio extends LeaderShipCardProps {
  ratio?: string;
}

const LeaderShipCard: FC<LeaderShipCardPropsWithRatio> = ({ image, carousel, name, role, ratio = 'Square', link }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = 3;

  const changeSlide = () => {
    setCurrentSlide(prevSlide => (prevSlide === 2 ? 0 : prevSlide + 1));
  };

  return (
    <div className={styles.container}>
      {ratio !== 'Tall' && (
        <article className={styles.hover_action}>
          <Link href={link || ''} className={styles.link}>
            <div className={styles.image}>
              <Image
                url={image?.url || ''}
                alt={image?.alt}
                width={image?.width || 500}
                height={image?.height || 500}
                className={styles[ratio]}
              />
            </div>
            {name && <div className={styles.heading}>{name}</div>}
            {role && <div className={styles.role}>{role}</div>}
          </Link>
        </article>
      )}
      {ratio === 'Tall' && (
        <div>
          <div className={styles.carousel} 
            tabIndex={0} 
            role="button" 
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                changeSlide();
              }
            }}
          >
            <div className={`${styles.slidesWrapper} ${currentSlide === 2 ? styles.addborder : ''}`}>
              <div
                className={`${styles[ratio]} ${styles.slides}`}
                role="button"
                onClick={changeSlide}
                tabIndex={-1}
                style={{
                  transform: `translateX(-${currentSlide * 100}%)`,
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    changeSlide();
                  }
                }}
              >
                <div className={styles.slide}>
                  <video
                    src={carousel?.videoFile}
                    preload="auto"
                    autoPlay={true}
                    loop={true}
                    muted={true}
                    playsInline={true}
                  >
                    <track kind="captions" srcLang="en" src="#" default />
                  </video>
                  <Image
                    url={image?.url}
                    alt={image?.alt || 'MapImage here'}
                    width={image?.width || 500}
                    height={image?.height || 500}
                    className={styles.static_image}
                  />
                </div>
                <div className={styles.slide}>
                  <Image
                    url={carousel?.mapImage?.url}
                    alt={carousel?.mapImage?.alt || 'MapImage here'}
                    width={carousel?.mapImage?.width || 500}
                    height={carousel?.mapImage?.height || 500}
                  />
                </div>
                <div className={styles.slide}>
                  <div className={styles.padding}>
                    <div className={styles.experience}>{carousel?.experience}</div>
                  </div>
                </div>
              </div>
              <div className={styles.dots}>
                {Array.from({ length: totalSlides }).map((_, index) => (
                  <button
                    key={index}
                    className={`${styles.carousel_dot} ${currentSlide === index ? styles.carousel_dot_selected : ''}`}
                    onClick={e => {
                      e.stopPropagation();
                      setCurrentSlide(index);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        setCurrentSlide(index);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {name && <div className={`${styles.heading} ${styles.tallHeading}`}>{name}</div>}
          {role && <span className={styles.role}>{role}</span>}
        </div>
      )}
    </div>
  );
};

export default LeaderShipCard;
